import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError, apiFetch } from "@/lib/api";

import type { LinkedIncident } from "./casesApi";
import { linkQualityCaseIncident, unlinkQualityCaseIncident } from "./casesApi";
import { QualityIncidentPicker } from "./QualityIncidentPicker";
import { QualityLinkedNcrTable } from "./QualityLinkedNcrTable";

const NCR_TYPES = ["pcba", "clamp", "device"] as const;

export function QualityCaseNcrsCard({
  caseId,
  incidents,
  writesAllowed,
  onChanged,
}: {
  caseId: number;
  incidents: LinkedIncident[];
  writesAllowed: boolean;
  onChanged: () => Promise<void>;
}): JSX.Element {
  const { showToast } = useHubToast();
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [type, setType] = useState<(typeof NCR_TYPES)[number]>("device");
  const [issue, setIssue] = useState("");
  const [serial, setSerial] = useState("");

  const linkMutation = useMutation({
    mutationFn: (incidentId: number) => linkQualityCaseIncident(caseId, incidentId),
    onSuccess: async () => {
      showToast({ tone: "success", message: "NCR linked." });
      await onChanged();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const unlinkMutation = useMutation({
    mutationFn: (incidentId: number) => unlinkQualityCaseIncident(caseId, incidentId),
    onSuccess: async () => {
      showToast({ tone: "success", message: "NCR unlinked." });
      await onChanged();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const created = await apiFetch<{ id: number }>("/api/manufacturing/qc/incidents", {
        method: "POST",
        body: {
          type,
          issue: issue.trim(),
          serial: serial.trim(),
          device_label_serial: type === "device" ? serial.trim() : undefined,
          clamp_serial: type === "clamp" ? serial.trim() : undefined,
        },
      });
      if (created?.id == null) {
        throw new Error("The NCR was created without an id.");
      }
      await linkQualityCaseIncident(caseId, created.id);
      return created.id;
    },
    onSuccess: async (incidentId) => {
      setShowCreate(false);
      setIssue("");
      setSerial("");
      showToast({ tone: "success", message: `Created NCR ${incidentId} and linked it.` });
      await onChanged();
    },
    onError: (error: unknown) => {
      const message = error instanceof ApiError || error instanceof Error ? error.message : "Could not create the NCR.";
      showToast({ tone: "error", message });
    },
  });

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return incidents.filter((incident) => {
      if (needle === "") return true;
      const haystack = [
        String(incident.id),
        incident.issue,
        incident.repairScrap,
        incident.open ? "open" : "closed",
      ]
        .filter((part) => part != null)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [incidents, search]);

  const tableRows = rows.map((incident) => ({
    id: incident.id,
    issue: incident.issue,
    issueDate: incident.issueDate,
    type: incident.type,
    pcbaSerial: incident.pcbaSerial,
    clampSerial: incident.clampSerial,
    deviceLabelSerial: incident.deviceLabelSerial,
    repairScrap: incident.repairScrap,
    open: incident.open,
    rcaId: incident.rcaId,
  }));

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center gap-2">
        <CardTitleWithHelp hoverHint="NCRs linked to this case. The number is the quality incident id. Link one that already exists, or open a new QC incident and link it.">
          Linked NCRs
        </CardTitleWithHelp>
        {writesAllowed && !showCreate ? (
          <Button size="sm" variant="secondary" onClick={() => setShowCreate(true)}>
            New NCR
          </Button>
        ) : null}
      </CardHeader>
      <CardBody className="space-y-4">
        <div>
          <Label htmlFor={`case-${caseId}-ncr-search`}>Search NCRs</Label>
          <Input
            id={`case-${caseId}-ncr-search`}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter by number, status, or disposition"
          />
        </div>
        {writesAllowed ? (
          <QualityIncidentPicker
            id={`case-${caseId}-ncr-picker`}
            values={incidents.map((incident) => incident.id)}
            knownLabels={Object.fromEntries(incidents.map((incident) => [incident.id, String(incident.id)]))}
            onChange={(next) => {
              const linked = new Set(incidents.map((incident) => incident.id));
              const added = next.find((incidentId) => !linked.has(incidentId));
              const removed = incidents.find((incident) => !next.includes(incident.id));
              if (added != null) linkMutation.mutate(added);
              else if (removed != null) unlinkMutation.mutate(removed.id);
            }}
          />
        ) : null}
        {writesAllowed && showCreate ? (
          <div className="space-y-3 rounded-md border border-slate-200 p-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor={`case-${caseId}-ncr-type`}>Type</Label>
                <select
                  id={`case-${caseId}-ncr-type`}
                  className="mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                  value={type}
                  onChange={(event) => setType(event.target.value as (typeof NCR_TYPES)[number])}
                >
                  {NCR_TYPES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor={`case-${caseId}-ncr-serial`}>Serial</Label>
                <Input
                  id={`case-${caseId}-ncr-serial`}
                  value={serial}
                  onChange={(event) => setSerial(event.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor={`case-${caseId}-ncr-issue`}>Issue</Label>
              <TextArea
                id={`case-${caseId}-ncr-issue`}
                value={issue}
                rows={3}
                onChange={(event) => setIssue(event.target.value)}
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button
                disabled={createMutation.isPending || issue.trim() === "" || serial.trim() === ""}
                onClick={() => createMutation.mutate()}
              >
                Create NCR
              </Button>
            </div>
          </div>
        ) : null}
        <QualityLinkedNcrTable
          ncrs={tableRows}
          emptyLabel={search.trim() !== "" ? "No rows match these filters." : "No NCRs linked to this case."}
        />
      </CardBody>
    </Card>
  );
}
